import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Megaphone, FileText, CheckCircle, TrendingUp, DollarSign, Users, Target, Building2 } from "lucide-react";
import { PeriodSelector, KPICard, IndicatorRow } from "@/components/UIComponents";
import { TrendModal } from "@/components/TrendModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency, formatPercent, formatNumber, MONTHS, CONFIG } from "@/lib/config";
import api from "@/lib/api";

export const Marketing = () => {
  const [year, setYear] = useState(2026);
  const [month, setMonth] = useState(1);
  const [quarter, setQuarter] = useState(1);
  const [view, setView] = useState("mensuel");
  const [selectedDept, setSelectedDept] = useState("GLOBAL");
  const [loading, setLoading] = useState(true);
  const [ratios, setRatios] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Modal de tendance
  const [showTrendModal, setShowTrendModal] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { getMonthsForPeriod, aggregateMonthlyData } = await import('@/lib/periodUtils');
        const periods = getMonthsForPeriod(view, year, month, quarter);
        
        const monthsData = await Promise.all(
          periods.map(p => api.getDashboard(p.year, p.month))
        );
        
        const dashboard = aggregateMonthlyData(monthsData);
        setDashboardData(dashboard);
        
        if (view === "mensuel") {
          const ratiosData = await api.getRatios(year, month);
          setRatios(ratiosData);
        }
      } catch (error) {
        console.error("Error:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [year, month, quarter, view]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  const commercial = ratios?.commercial || {};
  const globalKpi = dashboardData?.kpis?.GLOBAL || {};
  const revenus = globalKpi.revenus || {};

  // Valeurs estimées pour le marketing
  const objectifSoumissions = 25;
  const objectifContrats = 10;
  const objectifTauxSignature = 40;
  
  const cacGoogle = 150;
  const cacMeta = 200;
  const cacGlobal = (cacGoogle + cacMeta) / 2;
  const ltv = commercial.valeur_moyenne_contrat * 1.5 || 5000;
  const ltvCacRatio = ltv / cacGlobal;
  const romi = ((revenus.total - (cacGoogle * 10 + cacMeta * 5)) / (cacGoogle * 10 + cacMeta * 5) * 100) || 0;
  
  const abonnesFB = 2500;
  const abonnesIG = 1800;

  return (
    <div data-testid="marketing-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Ventes &amp; Marketing</h1>
          <p className="text-slate-500 mt-1">{MONTHS[month]} {year}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedDept} onValueChange={setSelectedDept}>
            <SelectTrigger className="w-48 bg-white">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="GLOBAL">📊 Tous départements</SelectItem>
              <SelectItem value="PRQC">🎨 {CONFIG.department_names.PRQC}</SelectItem>
              <SelectItem value="LMDS">🏪 {CONFIG.department_names.LMDS}</SelectItem>
              <SelectItem value="LOCATION">🚚 {CONFIG.department_names.LOCATION}</SelectItem>
            </SelectContent>
          </Select>
          <PeriodSelector 
            year={year} 
            setYear={setYear} 
            month={month} 
            setMonth={setMonth}
            view={view}
            setView={setView}
            quarter={quarter}
            setQuarter={setQuarter}
          />
        </div>
      </div>

      {/* KPIs Conversion */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in delay-100">
        <KPICard
          title="Soumissions Envoyées"
          value={formatNumber(commercial.soumissions_envoyees)}
          subtitle={`Objectif: ${objectifSoumissions}`}
          change={`${formatPercent(commercial.soumissions_envoyees / objectifSoumissions * 100)} atteint`}
          changeType={commercial.soumissions_envoyees >= objectifSoumissions ? 'positive' : 'neutral'}
          icon={FileText}
          color="blue"
          kpiKey="soumissions_envoyees"
          onClick={() => {
            setSelectedKPI({title: "Soumissions Envoyées", kpiKey: "soumissions_envoyees", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Contrats Signés"
          value={formatNumber(commercial.contrats_signes)}
          subtitle={`Objectif: ${objectifContrats}`}
          change={`${formatPercent(commercial.contrats_signes / objectifContrats * 100)} atteint`}
          changeType={commercial.contrats_signes >= objectifContrats ? 'positive' : 'neutral'}
          icon={CheckCircle}
          color="emerald"
          kpiKey="contrats_signes"
          onClick={() => {
            setSelectedKPI({title: "Contrats Signés", kpiKey: "contrats_signes", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Taux de Signature"
          value={formatPercent(commercial.taux_signature)}
          subtitle={`Cible: ${objectifTauxSignature}%`}
          change={commercial.taux_signature >= objectifTauxSignature ? "Excellente conversion" : "Amélioration possible"}
          changeType={commercial.taux_signature >= objectifTauxSignature ? 'positive' : 'negative'}
          icon={Target}
          color={commercial.taux_signature >= objectifTauxSignature ? "emerald" : "amber"}
          kpiKey="taux_signature"
          onClick={() => {
            setSelectedKPI({title: "Taux de Signature", kpiKey: "taux_signature", isPercent: true, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
        <KPICard
          title="Valeur Moyenne Contrat"
          value={formatCurrency(commercial.valeur_moyenne_contrat)}
          subtitle="Par contrat signé"
          icon={DollarSign}
          color="purple"
          kpiKey="valeur_moyenne_contrat"
          onClick={() => {
            setSelectedKPI({title: "Valeur Moyenne Contrat", kpiKey: "valeur_moyenne_contrat", isPercent: false, departement: selectedDept});
            setShowTrendModal(true);
          }}
        />
      </div>

      {/* Détails Marketing */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in delay-200">
        {/* Funnel de conversion */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Funnel de Conversion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Soumissions envoyées</span>
                  <span className="font-medium text-blue-600">{commercial.soumissions_envoyees}</span>
                </div>
                <Progress value={100} className="h-4 [&>div]:bg-blue-500" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Contrats signés</span>
                  <span className="font-medium text-emerald-600">{commercial.contrats_signes}</span>
                </div>
                <Progress value={commercial.taux_signature} className="h-4" />
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Taux de conversion global</span>
                  <span className={`text-xl font-bold ${commercial.taux_signature >= 40 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {formatPercent(commercial.taux_signature)}
                  </span>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600">Valeur totale des contrats signés:</p>
                <p className="kpi-value text-2xl text-emerald-600">{formatCurrency(revenus.bancaire)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Coût d'acquisition */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Coût d'Acquisition Client (CAC)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div 
                  className="bg-red-50 rounded-lg p-4 cursor-pointer hover:bg-red-100 transition-colors"
                  onClick={() => {
                    setSelectedKPI({title: "CAC Google", kpiKey: "cac_google", isPercent: false, departement: selectedDept});
                    setShowTrendModal(true);
                  }}
                >
                  <p className="text-xs text-red-600 mb-1">CAC Google</p>
                  <p className="kpi-value text-xl text-red-700">{formatCurrency(cacGoogle)}</p>
                  <p className="text-xs text-red-500 mt-1">Par client acquis</p>
                </div>
                <div 
                  className="bg-blue-50 rounded-lg p-4 cursor-pointer hover:bg-blue-100 transition-colors"
                  onClick={() => {
                    setSelectedKPI({title: "CAC Meta", kpiKey: "cac_meta", isPercent: false, departement: selectedDept});
                    setShowTrendModal(true);
                  }}
                >
                  <p className="text-xs text-blue-600 mb-1">CAC Meta</p>
                  <p className="kpi-value text-xl text-blue-700">{formatCurrency(cacMeta)}</p>
                  <p className="text-xs text-blue-500 mt-1">Par client acquis</p>
                </div>
              </div>
              
              <div className="border-t pt-4 space-y-3">
                <div 
                  className="flex justify-between cursor-pointer hover:bg-slate-50 p-2 rounded transition-colors"
                  onClick={() => {
                    setSelectedKPI({title: "LTV", kpiKey: "ltv", isPercent: false, departement: selectedDept});
                    setShowTrendModal(true);
                  }}
                >
                  <span className="text-slate-600">LTV (Valeur vie client)</span>
                  <span className="money-value font-medium text-emerald-600">{formatCurrency(ltv)}</span>
                </div>
                <div 
                  className="flex justify-between cursor-pointer hover:bg-slate-50 p-2 rounded transition-colors"
                  onClick={() => {
                    setSelectedKPI({title: "Ratio LTV/CAC", kpiKey: "ltv_cac_ratio", isPercent: false, departement: selectedDept});
                    setShowTrendModal(true);
                  }}
                >
                  <span className="text-slate-600">Ratio LTV:CAC</span>
                  <span className={`font-medium ${ltvCacRatio >= 3 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {ltvCacRatio.toFixed(1)}:1
                  </span>
                </div>
                <div 
                  className="flex justify-between cursor-pointer hover:bg-slate-50 p-2 rounded transition-colors"
                  onClick={() => {
                    setSelectedKPI({title: "ROMI", kpiKey: "romi", isPercent: true, departement: selectedDept});
                    setShowTrendModal(true);
                  }}
                >
                  <span className="text-slate-600">ROMI</span>
                  <span className={`font-medium ${romi >= 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {formatPercent(romi)}
                  </span>
                </div>
              </div>

              <div className="bg-amber-50 rounded-lg p-3 text-sm">
                <p className="text-amber-700">
                  <strong>Objectif LTV:CAC:</strong> Ratio ≥ 3:1 pour une acquisition rentable
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Réseaux sociaux */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Présence sur les Réseaux Sociaux</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-700">Facebook</span>
              </div>
              <p className="kpi-value text-2xl text-blue-800">{formatNumber(abonnesFB)}</p>
              <p className="text-xs text-blue-600">Abonnés</p>
            </div>
            <div className="bg-pink-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-5 h-5 text-pink-600" />
                <span className="font-medium text-pink-700">Instagram</span>
              </div>
              <p className="kpi-value text-2xl text-pink-800">{formatNumber(abonnesIG)}</p>
              <p className="text-xs text-pink-600">Abonnés</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                <span className="font-medium text-emerald-700">Clients Google</span>
              </div>
              <p className="kpi-value text-2xl text-emerald-800">~10</p>
              <p className="text-xs text-emerald-600">Ce mois</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-purple-700">Clients Meta</span>
              </div>
              <p className="kpi-value text-2xl text-purple-800">~5</p>
              <p className="text-xs text-purple-600">Ce mois</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal Tendance */}
      {showTrendModal && selectedKPI && (
        <TrendModal
          open={showTrendModal}
          onClose={() => setShowTrendModal(false)}
          title={selectedKPI.title}
          kpiKey={selectedKPI.kpiKey}
          departement={selectedKPI.departement}
          isPercent={selectedKPI.isPercent}
          chartType={selectedKPI.isPercent ? "line" : "bar"}
        />
      )}
    </div>
  );
};

export default Marketing;
